import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { projectApi } from "../api/client";
import type { Project } from "../types";
import "./ProjectsView.css";
import { useToast } from "../lib/toast";

export default function ProjectsView() {
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setProjects(await projectApi.list());
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await projectApi.create({ name, description, instructions });
      setName("");
      setDescription("");
      setInstructions("");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) =>
      [project.name, project.description, project.instructions]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [projects, search]);

  const visibleIds = filteredProjects.map((project) => project.id);
  const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length || deleting) return;
    if (!window.confirm(`Delete ${ids.length} selected project${ids.length === 1 ? "" : "s"}?`)) return;

    setDeleting(true);
    const results = await Promise.allSettled(ids.map((id) => projectApi.remove(id)));
    const failedIds = new Set(ids.filter((_, index) => results[index].status === "rejected"));
    const deletedCount = ids.length - failedIds.size;
    if (deletedCount) {
      setProjects((current) => current.filter((project) => !ids.includes(project.id) || failedIds.has(project.id)));
    }
    setSelectedIds(failedIds);
    setDeleting(false);

    if (failedIds.size) {
      toast.error(`Deleted ${deletedCount} project${deletedCount === 1 ? "" : "s"}; ${failedIds.size} failed.`);
    } else {
      toast.success(`Deleted ${deletedCount} project${deletedCount === 1 ? "" : "s"}.`);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">Projects</h2>
          <p className="view-subtitle">
            Group chats, files, instructions, and wikis
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? "Cancel" : "+ New Project"}
        </button>
      </div>

      {showForm && (
        <div className="card fade-in" style={{ marginBottom: "var(--gap-lg)" }}>
          <div className="col">
            <div>
              <label className="form-label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Project name"
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea
                className="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description"
              />
            </div>
            <div>
              <label className="form-label">Instructions (system prompt)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 140 }}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Custom instructions injected into chats…"
              />
            </div>
            <button className="btn btn-primary" onClick={create}>
              Create Project
            </button>
          </div>
        </div>
      )}

      {projects.length > 0 && (
        <div className="projects-toolbar" aria-label="Project tools">
          <label className="projects-search">
            <span className="sr-only">Search projects</span>
            <input
              className="input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects"
              aria-label="Search projects"
            />
          </label>
          <div className="projects-selection-actions">
            <button className="btn btn-sm" onClick={toggleAllVisible} disabled={!visibleIds.length}>
              {allVisibleSelected ? "Clear visible" : "Select all visible"}
            </button>
            {selectedIds.size > 0 && (
              <>
                <span className="projects-selected-count" aria-live="polite">
                  {selectedIds.size} selected
                </span>
                <button className="btn btn-sm btn-danger" onClick={deleteSelected} disabled={deleting}>
                  {deleting ? "Deleting…" : `Delete ${selectedIds.size}`}
                </button>
                <button className="btn btn-sm" onClick={() => setSelectedIds(new Set())} disabled={deleting}>
                  Clear selection
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading && <div className="empty-state"><span className="spinner" /></div>}
      {!loading && projects.length === 0 && (
        <div className="empty-state">
          <h3>No projects yet</h3>
          <p>Create one to organize related chats and files.</p>
        </div>
      )}

      {!loading && projects.length > 0 && filteredProjects.length === 0 && (
        <div className="empty-state">
          <h3>No matching projects</h3>
          <p>Try a different search.</p>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
        {filteredProjects.map((p) => (
          <div key={p.id} className={`card project-card${selectedIds.has(p.id) ? " is-selected" : ""}`}>
            <div className="project-card-topline">
              <input
                className="project-card-check"
                type="checkbox"
                checked={selectedIds.has(p.id)}
                onChange={() => toggleSelected(p.id)}
                aria-label={`Select ${p.name}`}
              />
              <Link to={`/projects/${p.id}`} className="project-card-link">
                <h3 className="project-card-name">{p.name}</h3>
              </Link>
            </div>
            <Link to={`/projects/${p.id}`} className="project-card-link project-card-body">
              <p className="project-card-desc muted">
                {p.description || "No description"}
              </p>
              <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>
                {new Date(p.created_at).toLocaleDateString()}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
